import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import API_BASE_URL from "../../utils/api";

const formatMinutes = (minutes = 0) => `${minutes.toLocaleString()} min`;

const AdminContributionAnalytics = () => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `${API_BASE_URL}/transcribeathon/admin/contribution-analytics-global`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setData(res.data);
      } catch (err) {
        console.error("Failed to load global contribution analytics", err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Failed to load contribution analytics.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        Global Contribution Analytics
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        {data.note}
      </Alert>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(5, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryCard
          title="Total Time"
          value={formatMinutes(data.totals.totalDurationMinutes)}
        />
        <SummaryCard title="Contributions" value={data.totals.contributions} />
        <SummaryCard title="Users" value={data.totals.users} />
        <SummaryCard title="Documents" value={data.totals.documents} />
        <SummaryCard title="Reviewed" value={data.totals.reviewedCount} />
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, value) => setTab(value)}>
          <Tab label="By User" />
          <Tab label="By Document" />
          <Tab label="Page Level" />
        </Tabs>
      </Paper>

      {tab === 0 && <UserTable rows={data.users} />}
      {tab === 1 && <DocumentTable rows={data.documents} />}
      {tab === 2 && <PageLevelTable rows={data.pageRows} />}
    </Box>
  );
};

const SummaryCard = ({ title, value }) => (
  <Paper sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Paper>
);

const UserTable = ({ rows }) => (
  <Paper sx={{ overflow: "auto" }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Email</TableCell>
          <TableCell align="right">Transcribed</TableCell>
          <TableCell align="right">Reviewed</TableCell>
          <TableCell align="right">Saved</TableCell>
          <TableCell align="right">Total Time</TableCell>
          <TableCell align="right">Points</TableCell>
          <TableCell align="right">Participant Rows</TableCell>
          <TableCell align="right">Non-participant Rows</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.userId}>
            <TableCell>{row.userName}</TableCell>
            <TableCell>{row.userEmail}</TableCell>
            <TableCell align="right">{row.transcribedCount}</TableCell>
            <TableCell align="right">{row.reviewedCount}</TableCell>
            <TableCell align="right">{row.savedProgressCount}</TableCell>
            <TableCell align="right">
              {formatMinutes(row.totalDurationMinutes)}
            </TableCell>
            <TableCell align="right">{row.totalPoints}</TableCell>
            <TableCell align="right">{row.participantRows}</TableCell>
            <TableCell align="right">{row.nonParticipantRows}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const DocumentTable = ({ rows }) => (
  <Paper sx={{ overflow: "auto" }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Document</TableCell>
          <TableCell>Document ID</TableCell>
          <TableCell align="right">Pages Worked</TableCell>
          <TableCell align="right">Users Worked</TableCell>
          <TableCell align="right">Transcribed</TableCell>
          <TableCell align="right">Reviewed</TableCell>
          <TableCell align="right">Saved</TableCell>
          <TableCell align="right">Total Time</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.documentId}>
            <TableCell>{row.documentTitle}</TableCell>
            <TableCell>{row.documentId}</TableCell>
            <TableCell align="right">{row.pagesWorked}</TableCell>
            <TableCell align="right">{row.usersWorked}</TableCell>
            <TableCell align="right">{row.transcribedCount}</TableCell>
            <TableCell align="right">{row.reviewedCount}</TableCell>
            <TableCell align="right">{row.savedProgressCount}</TableCell>
            <TableCell align="right">
              {formatMinutes(row.totalDurationMinutes)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const PageLevelTable = ({ rows }) => (
  <Paper sx={{ overflow: "auto" }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Document</TableCell>
          <TableCell align="right">Page</TableCell>
          <TableCell>User</TableCell>
          <TableCell>Participant?</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Content Type</TableCell>
          <TableCell align="right">Time</TableCell>
          <TableCell>Finished At</TableCell>
          <TableCell>Suspicious?</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.contributionId}>
            <TableCell>{row.documentTitle}</TableCell>
            <TableCell align="right">{row.pageNumber}</TableCell>
            <TableCell>{row.userName}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={row.isParticipant ? "Participant" : "Non-participant"}
                color={row.isParticipant ? "success" : "default"}
              />
            </TableCell>
            <TableCell>{row.type}</TableCell>
            <TableCell>{row.pageContentType}</TableCell>
            <TableCell align="right">
              {formatMinutes(row.durationMinutes)}
            </TableCell>
            <TableCell>
              {row.finishedAt ? new Date(row.finishedAt).toLocaleString() : "-"}
            </TableCell>
            <TableCell>
              {row.suspiciousSpeed ? (
                <Chip size="small" label="Yes" color="warning" />
              ) : (
                "No"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

export default AdminContributionAnalytics;
